/**
 * App-surface click layer (Aug 21 2026, Nitish-approved identified product analytics).
 *
 * ONE delegated document-level listener: any element carrying a `data-track`
 * attribute fires a single `app_click` event with CONTROLLED properties.
 * Adding tracking to a new CTA = add data-track="surface_element" to the
 * element (optionally data-track-surface) — never a new event name, and never
 * free text: chat titles / employee names / user content must not leak into
 * analytics properties.
 *
 * Events go through trackEvent (GA4 + PostHog dual-fire, same as everything
 * else). For logged-in behavior questions, PostHog is the system of record:
 * signed-in real customers are identified by uid (AuthContext), so app_click
 * joins into per-account journeys. Taxonomy: js/cellcog/TRACKING.md
 * ("App surfaces").
 */
import { useEffect } from 'react';
import { trackEvent } from './analytics';

let installed = false;

/**
 * app_view (Sep 7 2026, Nitish: "what are people clicking on in the app"): the DENOMINATOR for
 * app_click. Fire once when a tracked surface mounts (the surface names match app_click's
 * `surface` values: ec · tasks · channels · projects · outreach · connectors ...), so every
 * click number reads as "clicks per person who saw the surface", not per user overall.
 * Controlled identifiers only, same rule as app_click.
 */
/** Route TEMPLATE for analytics (Sep 8 2026, Nitish's GA4 dimension pass): 24-hex ObjectIds
 *  collapse to `:id` (/ai_employee/6a64ca... -> /ai_employee/:id) so the `page_path` custom
 *  dimension stays far below GA4's ~500-distinct-values/day "(other)" ceiling. */
export function templatePath(pathname) {
  return String(pathname || '').replace(/[0-9a-f]{24}(?=\/|$)/gi, ':id');
}

export function trackAppView(surface) {
  try {
    trackEvent('app_view', { surface, page_path: templatePath(window.location.pathname) });
  } catch (err) { /* analytics must never break product flows */ }
}

/** Hook form: `useAppView('tasks')` at the top of a page/surface component. */
export function useAppView(surface) {
  useEffect(() => { trackAppView(surface); }, [surface]);
}

export function installAppClickTracking() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('click', (e) => {
    try {
      const el = e.target && e.target.closest && e.target.closest('[data-track]');
      if (!el) return;
      const name = el.getAttribute('data-track');
      if (!name) return;
      // Surface resolution (Aug 28 2026, screen-source instrumentation): closest()
      // includes the element itself, so an explicit data-track-surface on the element
      // still wins; else the nearest ANCESTOR carrying data-track-surface names the
      // screen (New Agent vs New AI Employee vs the fork all render the same shared
      // composer elements — and New Agent and the fork both live at "/", so page_path
      // alone cannot separate them); else the element-name prefix, as before.
      const host = el.closest('[data-track-surface]');
      const props = {
        element: name,
        surface: (host && host.getAttribute('data-track-surface'))
          || (name.indexOf('_') > 0 ? name.slice(0, name.indexOf('_')) : 'app'),
        page_path: templatePath(window.location.pathname),
      };
      // Optional CONTROLLED context (CEL-2992, Sep 28 2026): data-track-detail on the element
      // (a word like manage / on / all, or a catalog slug, never user text), plus the nearest
      // ancestor's data-track-state (none / some / many, ready / not_ready) and
      // data-track-container (popover / sheet).
      const detail = el.getAttribute('data-track-detail');
      if (detail) props.detail = detail;
      const stateHost = el.closest('[data-track-state]');
      if (stateHost) props.state = stateHost.getAttribute('data-track-state');
      const containerHost = el.closest('[data-track-container]');
      if (containerHost) props.container = containerHost.getAttribute('data-track-container');
      trackEvent('app_click', props);
    } catch (err) { /* analytics must never break product flows */ }
  }, true); // capture phase: fires even when a component handler stops propagation (antd menus do)
}

const appClickTracking = { installAppClickTracking };
export default appClickTracking;
